import {
  Box,
  Typography,
  Switch,
  FormControlLabel,
  Paper,
  useTheme,
  useMediaQuery,
  CircularProgress,
} from "@mui/material"
import { useState, useEffect } from "react"
import { FaCamera } from "react-icons/fa"
import { IoIosArrowForward } from "react-icons/io"
import { toast } from "react-toastify"
import { configsService } from "services/configsService"
import { IConfigDataRequest, IConfigDataResponse } from "interfaces/IConfig"
import { ContainerPage } from "components/ContainerPage"
import { BoxUrl, BoxLink, BoxPlace, StatusIndicator, StatusDot } from "./styles"

export const Scanner = () => {
  const [isLoading, setIsLoading] = useState(false)
  const [useScanner, setUseScanner] = useState(false)
  const [configResponse, setConfigResponse] = useState<IConfigDataResponse | null>(null)
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"))

  const handleGetConfigs = async () => {
    try {
      setIsLoading(true)
      const response = await configsService.getConfig()
      setConfigResponse(response)
      setUseScanner(response.useScanner)
    } catch (error: any) {
      toast(error?.message ?? "Deu erro pra buscar as configurações, avisa o time de tech.", {
        type: "error",
      })
    } finally {
      setIsLoading(false)
    }
  }

  const handleUpdateConfigs = async (newUseScanner: boolean) => {
    try {
      setIsLoading(true)
      const configToUpdate: IConfigDataRequest = {
        useScanner: newUseScanner,
        ...(configResponse?.totalMarkets !== undefined && {
          totalMarkets: configResponse.totalMarkets,
        }),
      }

      await configsService.postConfig(configToUpdate)
      toast("Configuração do scanner atualizada com sucesso!", {
        type: "success",
      })
      setUseScanner(newUseScanner)
    } catch (error: any) {
      toast(error?.message ?? "Deu erro pra atualizar as configurações, avisa o time de tech.", {
        type: "error",
      })
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    handleGetConfigs()
  }, [])

  return (
    <ContainerPage>
      <Box>
        <BoxUrl>
          <BoxLink to="../configuracoes">Configurações</BoxLink>
          <IoIosArrowForward fontSize="1.125rem" />
          <BoxPlace>Scanner</BoxPlace>
        </BoxUrl>

        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, sm: 3 },
            borderRadius: 2,
            border: "1px solid #e0e0e0",
            backgroundColor: "#fff",
            position: "relative",
            minHeight: "300px",
          }}
        >
          {isLoading ? (
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "rgba(255, 255, 255, 0.8)",
                zIndex: 1,
              }}
            >
              <CircularProgress color="success" />
            </Box>
          ) : (
            <>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  mb: 3,
                  flexDirection: { xs: "column", sm: "row" },
                  gap: { xs: 2, sm: 0 },
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    width: { xs: "100%", sm: "auto" },
                  }}
                >
                  <FaCamera size={24} color="#757575" />
                  <Typography
                    variant="h6"
                    sx={{
                      ml: 2,
                      fontSize: { xs: "1.1rem", sm: "1.25rem" },
                      color: "#353535",
                      textAlign: { xs: "center", sm: "left" },
                      width: { xs: "100%", sm: "auto" },
                    }}
                  >
                    Configurações do Scanner
                  </Typography>
                </Box>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                  p: { xs: 1.5, sm: 2 },
                  backgroundColor: "#f5f5f5",
                  borderRadius: 1,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    flexDirection: { xs: "column", sm: "row" },
                    gap: { xs: 2, sm: 0 },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      width: { xs: "100%", sm: "auto" },
                      justifyContent: { xs: "center", sm: "flex-start" },
                    }}
                  >
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 500,
                        color: "#353535",
                        fontSize: { xs: "0.9rem", sm: "1rem" },
                      }}
                    >
                      Status do Scanner
                    </Typography>
                    <StatusIndicator active={useScanner}>
                      <StatusDot active={useScanner} />
                      {useScanner ? "Ativo" : "Inativo"}
                    </StatusIndicator>
                  </Box>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={useScanner}
                        onChange={() => handleUpdateConfigs(!useScanner)}
                        disabled={isLoading}
                        color="success"
                        size={isMobile ? "small" : "medium"}
                      />
                    }
                    label={
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 500,
                          color: "#353535",
                          fontSize: { xs: "0.9rem", sm: "1rem" },
                        }}
                      >
                        {useScanner ? "Desativar Scanner" : "Ativar Scanner"}
                      </Typography>
                    }
                    sx={{
                      margin: { xs: "0 auto", sm: 0 },
                    }}
                  />
                </Box>

                <Box
                  sx={{
                    mt: { xs: 1, sm: 2 },
                    p: { xs: 1.5, sm: 2 },
                    backgroundColor: "#fff",
                    borderRadius: 1,
                    border: "1px solid #e0e0e0",
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#757575",
                      lineHeight: 1.5,
                      fontSize: { xs: "0.875rem", sm: "1rem" },
                      textAlign: { xs: "center", sm: "left" },
                    }}
                  >
                    {useScanner
                      ? "O scanner está atualmente ativo e pronto para uso. Você pode escanear códigos QR usando a câmera do dispositivo."
                      : "O scanner está atualmente inativo. Ative-o para permitir o escaneamento de códigos QR usando a câmera do dispositivo."}
                  </Typography>
                </Box>
              </Box>
            </>
          )}
        </Paper>
      </Box>
    </ContainerPage>
  )
}
